import {
  Button,
  Grid,
  Loader,
  ScrollView,
  SearchField,
} from "@aws-amplify/ui-react";
import { API, graphqlOperation } from "aws-amplify";

import { useContext, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import LocalizedStrings from "react-localization";
import { FiArrowLeft, FiArrowRight } from "react-icons/fi";
import moment from "moment";
import {
  CCard,
  CCardBody,
  CCollapse,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
  CTable,
} from "@coreui/react";

import { listRoles, rolesByCompanyID } from "graphql/queries";
import { deleteRole } from "graphql/mutations";
import { customGetRole, getCompanyName } from "graphql/custom-queries";
import usePermissions from "hooks/usePermission";
import { globalStrings } from "config/translations/es";
import { RoleCreateForm, RoleUpdateForm } from ".";
import { LayoutContext } from "layout/context/LayoutContext";
import CompanyBranchFilter from "components/companies/CompanyBranchFilter";

const Roles = () => {
  const { me } = useContext(LayoutContext);
  const [canCreate, canUpdate, canDelete] = usePermissions([
    "role:create",
    "role:update",
    "role:delete",
  ]);
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [roleDetails, setRoleDetails] = useState();
  const [paginationTokens, setPaginationTokens] = useState({
    previous: [],
    nextToken: null,
  });
  const [modalDetails, setModalDetails] = useState({
    visible: false,
    roleId: null,
    roleName: null,
  });
  const [modalRole, setModalRole] = useState({
    visible: false,
    roleId: null,
  });
  const [searchBy, setSearchBy] = useState({
    company: {
      id: "",
      display: "",
    },
  });
  const columnsMapped = useMemo(
    () =>
      roles.map((item) => ({
        ...item,
        companyName: item.companyID ? item.companyName || <Loader /> : "",
        actions: (
          <>
            <Button
              className="px-1"
              children={
                roleDetails?.id === item.id
                  ? strings.actions.hide
                  : strings.actions.show
              }
              variation="link"
              size="small"
              onClick={() => {
                roleDetails?.id === item.id
                  ? setRoleDetails(null)
                  : fetchRole(item.id);
              }}
            />
            {canUpdate && (
              <Button
                className="px-1"
                children={strings.actions.edit}
                variation="link"
                size="small"
                onClick={() => openModal("update", item.id)}
              />
            )}
            {canDelete && (
              <Button
                className="px-1"
                children={strings.actions.delete}
                variation="link"
                size="small"
                onClick={() => {
                  setModalDetails({
                    visible: true,
                    roleId: item.id,
                    roleName: item.name,
                  });
                }}
              />
            )}
          </>
        ),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [roles, roleDetails?.id]
  );

  const onNext = () => {
    fetchRoles(paginationTokens.nextToken);
  };
  const onPrevious = () => {
    const previous =
      paginationTokens.previous?.length <= 1
        ? null
        : paginationTokens.previous?.slice(-2)[0];
    fetchRoles(previous, false);
  };
  const onKeywordSubmit = (keyword) => {
    const keywordSplit = keyword && keyword.split(" ");
    fetchRoles(undefined, undefined, keywordSplit);
  };
  const openModal = (action, id) =>
    setModalRole({ visible: true, action, roleId: id });
  const onSuccessAction = () => {
    setModalRole({ visible: false });
    fetchRoles();
  };

  async function fetchCompanyName(id) {
    try {
      const companyData = await API.graphql(
        graphqlOperation(getCompanyName, {
          id,
        })
      );
      const companyItem = companyData.data.getCompany;
      return companyItem?.name ?? "";
    } catch (err) {
      console.log("Error fetchCompanyName", err);
      return "";
    }
  }

  const getNamesByRoles = (list) => {
    list?.forEach(async (item) => {
      const companyName = await fetchCompanyName(item.companyID);

      setRoles((prevList) => {
        const updatedList = [...prevList];
        const index = updatedList.findIndex(
          (itemInd) => itemInd.id === item.id
        );

        updatedList[index] = {
          ...updatedList[index],
          companyName,
        };

        return updatedList;
      });
    });
  };

  async function fetchRoles(token, isNext = true, keywords) {
    let objOperation = {};
    let nameOperation = listRoles;
    let nameOperationStr = "listRoles";
    try {
      setRolesLoading(true);

      if (!!searchBy.company.id || !!me.companyID) {
        objOperation = {
          companyID: searchBy.company.id || me.companyID,
        };
        nameOperation = rolesByCompanyID;
        nameOperationStr = "rolesByCompanyID";
      }

      const rolesData = await API.graphql(
        graphqlOperation(nameOperation, {
          ...objOperation,
          limit: !keywords ? 10 : undefined,
          nextToken: token,
          filter: keywords
            ? {
                and: keywords.map((key) => ({
                  or: [
                    {
                      name: { contains: key },
                    },
                    {
                      description: { contains: key },
                    },
                  ],
                })),
              }
            : {},
        })
      );
      const rolesItems = rolesData.data[nameOperationStr].items;
      setRoles(rolesItems);
      getNamesByRoles(rolesItems);
      setPaginationTokens((prevSt) => {
        let previous = prevSt.previous ?? [];
        if (isNext) previous = token ? [...previous, token] : [...previous];
        if (!isNext && previous?.length) previous.pop();

        return {
          previous,
          nextToken: rolesData.data[nameOperationStr].nextToken,
        };
      });
      setRolesLoading(false);
    } catch (err) {
      toast.error(strings.responses.errorFetch);
      setRolesLoading(false);
    }
  }
  async function fetchRole(id) {
    try {
      const roleData = await API.graphql(
        graphqlOperation(customGetRole, {
          id,
        })
      );
      const roleItem = roleData.data.getRole;
      setRoleDetails({
        ...roleItem,
        permissions: roleItem.permissions?.items?.map(
          (item) => item.permission.name
        ),
        users: roleItem.users?.items?.map((item) => item.user.email),
      });
    } catch (err) {
      toast.error(strings.responses.errorFetch);
    }
  }
  async function roleDelete(id) {
    try {
      const result = await API.graphql(
        graphqlOperation(deleteRole, { input: { id } })
      );
      toast.success(strings.successDeleted);
      const deletedId = result.data.deleteRole.id;
      setRoles(roles.filter((item) => item.id !== deletedId));
      setModalDetails({
        visible: false,
        roleId: null,
      });
    } catch (err) {
      toast.error(strings.responses[err]);
    }
  }

  useEffect(() => {
    fetchRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchBy.company.id]);

  const tableColumns = [
    {
      key: "companyName",
      label: strings.table.headers.companyName,
      _style: { width: "30%" },
    },
    {
      key: "name",
      label: strings.table.headers.name,
      _style: { width: "20%" },
    },
    {
      key: "description",
      label: strings.table.headers.description,
      _style: { width: "30%" },
    },
    {
      key: "actions",
      label: "",
      _style: { width: "20%" },
    },
  ];

  return (
    <div>
      <span className="fs-3">{strings.title}</span>
      {canCreate && (
        <Button
          style={styles.buttonAdd}
          children={strings.actions.addRole}
          variation="primary"
          onClick={() => openModal("create")}
        />
      )}
      <div className="p-3">
        <Grid
          className="mb-3"
          templateColumns={{ medium: "1fr", xl: "1fr 1fr 1fr" }}
          rowGap="15px"
          columnGap="15px"
        >
          <CompanyBranchFilter
            searchBy={searchBy}
            setSearchBy={setSearchBy}
            onlyCompany
          />
          <SearchField
            label={strings.table.search.label}
            placeholder={strings.table.search.placeholder}
            onSubmit={onKeywordSubmit}
            onClear={onKeywordSubmit}
          />
        </Grid>
        {rolesLoading ? (
          <Loader />
        ) : (
          <>
            <CTable
              className="table-centered"
              items={columnsMapped}
              columns={tableColumns}
              tableHeadProps={{ color: "light" }}
              responsive
              hover
            />
            <Button
              size="small"
              disabled={paginationTokens.previous?.length <= 0}
              style={styles.buttonPrevious}
              onClick={onPrevious}
            >
              <FiArrowLeft /> {strings.table.pagination.buttonPrevious}
            </Button>
            <Button
              size="small"
              disabled={!paginationTokens.nextToken}
              style={styles.buttonNext}
              onClick={onNext}
            >
              <FiArrowRight /> {strings.table.pagination.buttonNext}
            </Button>
          </>
        )}
        <CCollapse visible={!!roleDetails?.id} style={styles.collapseCard}>
          <CCard key={roleDetails?.id}>
            <CCardBody>
              <h4 className="text-muted">
                {strings.details.title} "{roleDetails?.name}"
              </h4>
              <p className="text-muted">
                <b>{strings.details.description}:</b> {roleDetails?.description}
              </p>
              <div className="text-muted">
                <b>{strings.details.permissions}:</b>
                <ul>
                  {roleDetails?.permissions?.map((per, index) => (
                    <li key={index}>{globalStrings.permissions[per]}</li>
                  ))}
                </ul>
              </div>
              <div className="text-muted">
                <b>{strings.details.users}:</b>
                <ul>
                  {roleDetails?.users?.map((per, index) => (
                    <li key={index}>{per}</li>
                  ))}
                </ul>
              </div>
              <p className="text-muted">
                <b>{strings.details.createdAt}:</b>{" "}
                {moment(roleDetails?.createdAt).format("YYYY-MM-DD HH:mm:ss")}
              </p>
            </CCardBody>
          </CCard>
        </CCollapse>
      </div>
      <CModal
        alignment="center"
        visible={modalDetails.visible}
        onClose={() => setModalDetails({ visible: false, roleId: null })}
        backdrop="static"
      >
        <CModalBody>
          <b>
            {strings.formatString(strings.modal.confirmDeleteQuestion, {
              name: modalDetails.roleName,
            })}
          </b>
        </CModalBody>
        <CModalFooter>
          <Button
            size="small"
            onClick={() => setModalDetails({ visible: false, roleId: null })}
          >
            {strings.modal.cancelButton}
          </Button>
          <Button
            size="small"
            variation="primary"
            onClick={() => roleDelete(modalDetails.roleId)}
          >
            {strings.modal.confirmButton}
          </Button>
        </CModalFooter>
      </CModal>
      {modalRole.action && (
        <CModal
          size="xl"
          alignment="center"
          visible={modalRole.visible}
          onClose={() => setModalRole({ visible: false })}
          backdrop="static"
        >
          <CModalHeader>
            <CModalTitle>
              {modalRole.action === "create"
                ? strings.modalRole.addTitle
                : strings.modalRole.updateTitle}
            </CModalTitle>
          </CModalHeader>
          <CModalBody style={{ overflow: "hidden" }}>
            <ScrollView maxHeight="75vh">
              {modalRole.action === "create" ? (
                <RoleCreateForm onSuccess={onSuccessAction} />
              ) : (
                <RoleUpdateForm
                  roleId={modalRole.roleId}
                  onSuccess={onSuccessAction}
                />
              )}
            </ScrollView>
          </CModalBody>
        </CModal>
      )}
    </div>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Roles",
    successDeleted: "Rol eliminado con éxito",
    actions: {
      addRole: "Agregar Rol",
      show: "Ver",
      hide: "Ocultar",
      edit: "Editar",
      delete: "Eliminar",
    },
    responses: {
      errorFetch: "Error al consultar roles",
    },
    table: {
      headers: {
        companyName: "Empresa",
        name: "Nombre",
        description: "Descripción",
        actions: "Acciones",
      },
      pagination: {
        buttonPrevious: "Atrás",
        buttonNext: "Siguiente",
      },
      search: {
        label: "Buscar",
        placeholder: "Buscar por palabra clave...",
      },
    },
    details: {
      title: "Detalles del rol",
      description: "Descripción",
      permissions: "Permisos",
      users: "Usuarios",
      createdAt: "Fecha de creación",
    },
    modal: {
      confirmDeleteQuestion: "¿Estás seguro de eliminar el rol {name}?",
      cancelButton: "Cancelar",
      confirmButton: "Confirmar",
    },
    modalRole: {
      addTitle: "Agregar Rol",
      updateTitle: "Actualizar Rol",
    },
  },
});

const styles = {
  buttonAdd: { float: "right" },
  modal: { width: "250px" },
  collapseCard: { marginTop: "15px" },
  buttonPrevious: { marginLeft: "15px" },
  buttonNext: { float: "right", marginRight: "15px" },
  searchByKeyword: { marginBottom: "15px", width: "25%", minWidth: "300px" },
};

export default Roles;
